import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Plus, Trash2, Edit3, Search, X } from "lucide-react";
import api, { inrFormat, categoryLabel, apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Combobox from "../components/Combobox";

export default function AdminPage() {
  const { user, ready } = useAuth();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [stats, setStats] = useState(null);

  const load = async () => {
    const res = await api.get("/products", { params: { q, limit: 200 } });
    setProducts(res.data.products || []);
    const s = await api.get("/stats/summary");
    setStats(s.data);
  };

  useEffect(() => { if (ready && user?.role === "admin") load(); /* eslint-disable-next-line */ }, [q, ready, user]);

  if (!ready) return <div className="max-w-7xl mx-auto px-4 py-20 text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const delist = async (id) => {
    if (!window.confirm("Delist this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Delisted");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand">Admin</div>
          <h1 className="t-page">BHEGA Console</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Link to="/admin/hire-requests" className="btn-outline text-sm" data-testid="admin-hires-link">
            Hire Requests →
          </Link>
          <Link to="/admin/product-requests" className="btn-outline text-sm" data-testid="admin-product-requests-link">
            Product Requests →
          </Link>
          <Link to="/admin/bhega-csv" className="btn-outline text-sm" data-testid="admin-bhega-csv-link">
            Bulk Bhega CSV →
          </Link>
          <Link to="/admin/reports" className="btn-outline text-sm" data-testid="admin-reports-link">
            Reported messages →
          </Link>
          <button
            onClick={async () => {
              try {
                const res = await api.get("/admin/products/sample-csv", { responseType: "text" });
                const blob = new Blob([res.data], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "bhega-products-sample.csv";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (err) { toast.error(apiErrorMessage(err, "Sample CSV download failed")); }
            }}
            className="btn-outline text-sm"
            data-testid="admin-sample-csv"
          >
            Sample CSV
          </button>
          <label className="btn-outline text-sm cursor-pointer" data-testid="admin-bulk-csv-label">
            Bulk upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              data-testid="admin-bulk-csv-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  const res = await api.post("/admin/products/bulk-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success(`Imported ${res.data.inserted} product${res.data.inserted === 1 ? "" : "s"}${res.data.errors?.length ? `, ${res.data.errors.length} skipped` : ""}`);
                  load();
                } catch (err) {
                  toast.error(apiErrorMessage(err, "Bulk upload failed"));
                } finally {
                  e.target.value = "";
                }
              }}
            />
          </label>
          <button onClick={() => setCreating(true)} className="btn-primary" data-testid="admin-new-product">
            <Plus size={14} /> New product
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {[
            { label: "Products", v: stats.products },
            { label: "Users", v: stats.users },
            { label: "Bhega Joins", v: stats.bhega_joins ?? 0 },
            { label: "Room Messages", v: stats.room_messages ?? 0 },
          ].map((s) => (
            <div key={s.label} className="border border-border p-4 rounded-none" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</div>
              <div className="font-heading font-extrabold text-2xl">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 px-3 py-2 border border-border rounded-none w-full sm:w-96">
        <Search size={14} className="text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="flex-1 outline-none text-sm" data-testid="admin-product-search" />
      </div>

      <div className="mt-4 overflow-x-auto border border-border rounded-none">
        <table className="w-full text-sm">
          <thead className="bg-foreground/5 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Primary Tags</th>
              <th className="text-left px-4 py-3">Cities</th>
              <th className="text-left px-4 py-3">Bhega</th>
              <th className="text-left px-4 py-3">Comments</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const primary = (p.primary_tags && p.primary_tags.length) ? p.primary_tags : (p.tags || []).slice(0, 4);
              const cities = p.city_availability || [];
              return (
                <tr key={p.id} className="border-t border-border" data-testid={`admin-product-row-${p.id}`}>
                  <td className="px-4 py-3 align-top">
                    <div className="font-semibold">{p.product || p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.brand}</div>
                    {p.website_url && (
                      <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--bhega-info)] underline truncate block max-w-[200px]">
                        {p.website_url}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">{categoryLabel(p.category)}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {primary.slice(0, 4).map((t) => (
                        <span key={t} className="text-xs uppercase font-semibold px-1.5 py-0.5 bg-foreground/5 text-muted-foreground rounded-full">{t}</span>
                      ))}
                      {primary.length > 4 && <span className="text-xs text-muted-foreground">+{primary.length - 4}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                    {cities.length ? (
                      <>
                        {cities.slice(0, 2).join(" · ")}
                        {cities.length > 2 && <span className="text-muted-foreground"> +{cities.length - 2}</span>}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="inline-flex items-center gap-1 text-xs font-bold">
                      <span aria-hidden>✊</span>{p.bhega_count ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-xs">{p.comment_count ?? 0}</td>
                  <td className="px-4 py-3 align-top">
                    {p.is_delisted ? <span className="bhega-pill bg-[var(--bhega-error-soft)] text-[var(--bhega-error)]">Delisted</span> : <span className="bhega-pill bg-[var(--bhega-success-soft)] text-[var(--bhega-success)]">Live</span>}
                  </td>
                  <td className="px-4 py-3 text-right align-top whitespace-nowrap">
                    <button onClick={() => setEditing(p)} className="text-xs underline mr-3" data-testid={`admin-edit-${p.id}`}><Edit3 size={12} className="inline mr-1" />Edit</button>
                    <button onClick={() => delist(p.id)} className="text-xs underline text-[var(--bhega-error)]" data-testid={`admin-delist-${p.id}`}><Trash2 size={12} className="inline mr-1" />Delist</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(editing || creating) && (
        <ProductFormModal
          product={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function ProductFormModal({ product, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const base = {
      category: "automobiles", brand: "", name: "", product: "", typology: "", variant: "",
      price_min: "", price_max: "", fuel_type: "", transmission: "",
      city_availability: [], description: "", images: [], video_url: "", specs: {},
      tags: [],
    };
    if (!product) return base;
    return {
      ...base,
      ...product,
      product: product.product || product.name || "",
      typology: product.typology || product.variant || "",
      price_min: product.price_min ?? "",
      price_max: product.price_max ?? "",
      city_availability: product.city_availability || [],
      images: product.images || [],
      tags: product.tags || [],
    };
  });
  const [imgInput, setImgInput] = useState("");
  const [tagInput, setTagInput] = useState((product?.tags || []).join(", "));
  const [cityInput, setCityInput] = useState((product?.city_availability || []).join(", "));
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(false);

  // Load category suggestions once
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories || []));
  }, []);

  // Load brand suggestions whenever category changes
  useEffect(() => {
    if (!form.category) { setBrands([]); return; }
    api.get("/brands", { params: { category: form.category } })
      .then((r) => setBrands(r.data.brands || []));
  }, [form.category]);

  const formatApiError = (err) => apiErrorMessage(err, "Save failed");

  const buildPayload = () => {
    const cities = cityInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const primary_tags = (form.primary_tags_input ?? (Array.isArray(form.primary_tags) ? form.primary_tags.join(", ") : ""))
      .split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6);
    const variants_tags = (form.variants_tags_input ?? (Array.isArray(form.variants_tags) ? form.variants_tags.join(", ") : ""))
      .split(",").map((s) => s.trim()).filter(Boolean);

    const productName = (form.product || form.name || "").trim();
    const payload = {
      category: form.category,
      brand: (form.brand || "").trim(),
      name: productName,
      product: productName,
      typology: "",
      variant: "",
      primary_tags,
      variants_tags,
      website_url: (form.website_url || "").trim(),
      images: form.images || [],
      city_availability: cities,
      tags: [...primary_tags, ...variants_tags],
    };
    return payload;
  };

  const validate = (p) => {
    if (!p.brand) return "Brand is required";
    if (!p.name) return "Product (name) is required";
    if (p.price_max && p.price_min && p.price_max < p.price_min) return "Max price must be ≥ Min price";
    return null;
  };

  const save = async () => {
    const payload = buildPayload();
    const err = validate(payload);
    if (err) { toast.error(err); return; }

    setBusy(true);
    try {
      if (product) {
        await api.patch(`/products/${product.id}`, payload);
      } else {
        await api.post("/products", payload);
      }
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-2xl rounded-none border max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-product-form">
        <div className="flex justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h3 className="font-heading font-bold text-lg">{product ? "Edit" : "New"} product</h3>
          <button onClick={onClose} data-testid="admin-form-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase mb-1 block">Category *</label>
            <Combobox
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={categories.map((c) => ({ value: c.slug, label: c.label, hint: `${c.count} listings` }))}
              placeholder="Pick a category or create a new one"
              createLabel="Create new category"
              testId="admin-form-category"
            />
            <div className="text-xs text-muted-foreground mt-1">
              Choose from existing or type a new one like <code>mobile</code>, <code>watches</code>, <code>home_appliances</code>.
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase mb-1 block">Brand *</label>
            <Combobox
              value={form.brand}
              onChange={(v) => setForm({ ...form, brand: v })}
              options={brands.map((b) => ({ value: b }))}
              placeholder={form.category ? "Pick a brand or create a new one" : "Choose category first"}
              createLabel="Create new brand"
              testId="admin-form-brand"
              disabled={!form.category}
            />
            <div className="text-xs text-muted-foreground mt-1">Brand suggestions are filtered to the chosen category. Type any new brand to add it.</div>
          </div>
          <div className="col-span-2"><Inp label="Product *" v={form.product} onChange={(v) => setForm({ ...form, product: v, name: v })} testId="admin-form-name" /></div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase">Primary Tag (comma-separated, up to 6)</label>
            <textarea
              rows={2}
              value={form.primary_tags_input ?? (Array.isArray(form.primary_tags) ? form.primary_tags.join(", ") : "")}
              onChange={(e) => setForm({ ...form, primary_tags_input: e.target.value })}
              placeholder="e.g. Car, SUV, Luxury, Automobile, Petrol, Premium"
              className="input-bhega mt-1"
              data-testid="admin-form-primary-tags"
            />
            <div className="text-xs text-muted-foreground mt-1">These show as chips on the product card and drive smart search. Max 6 are kept.</div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase">Variants & Tags (comma-separated)</label>
            <textarea
              rows={2}
              value={form.variants_tags_input ?? (Array.isArray(form.variants_tags) ? form.variants_tags.join(", ") : "")}
              onChange={(e) => setForm({ ...form, variants_tags_input: e.target.value })}
              placeholder="e.g. 330Li M Sport, xDrive20d, xDrive30i"
              className="input-bhega mt-1"
              data-testid="admin-form-variants-tags"
            />
          </div>
          <div className="col-span-2">
            <Inp label="Original Website URL" v={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} testId="admin-form-website-url" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase">Cities (comma-separated)</label>
            <textarea
              rows={2}
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              placeholder="e.g. Mumbai, Maharashtra, Pune, Maharashtra, Bengaluru, Karnataka"
              className="input-bhega mt-1"
              data-testid="admin-form-cities"
            />
            <div className="text-xs text-muted-foreground mt-1">Add each city as &ldquo;City, State&rdquo;. Separate multiple cities with commas.</div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-card">
          <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="admin-product-save">{busy ? "Saving..." : (product ? "Update product" : "Create product")}</button>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, v, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="input-bhega mt-1" data-testid={testId} />
    </div>
  );
}
